import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../services/api';
import HelpTip from './HelpTip';

/**
 * «Pasar a recobro»: saca un expediente de AR y se lo pasa a ClaimUp.
 *
 * Quién lo ve lo decide el servidor (`GET /ar-exit/expedients/:id`): el admin,
 * la empresa dueña y sus gestores, solo en expedientes AR y solo si la empresa
 * tiene AR activado. Aquí no se repite la regla.
 *
 * No tiene vuelta atrás, así que pide confirmación y explica qué pasa.
 * Ver docs/ar-escalar-a-recobro-plan.md.
 */
function EscalateToRecoveryButton({ expedientId, expedientType, onEscalated }) {
  const [canEscalate, setCanEscalate] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get(`/ar-exit/expedients/${expedientId}`)
      .then(({ data }) => alive && setCanEscalate(Boolean(data?.data?.can_escalate)))
      .catch(() => alive && setCanEscalate(false));
    return () => {
      alive = false;
    };
  }, [expedientId, expedientType]);

  if (!canEscalate) return null;

  const escalate = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/ar-exit/expedients/${expedientId}/escalate`, {
        reason: reason.trim() || undefined,
      });
      toast.success('Expediente pasado a recobro: ahora lo gestiona ClaimUp');
      setConfirming(false);
      setCanEscalate(false);
      onEscalated?.(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type='button'
        onClick={() => setConfirming(true)}
        className='inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100'
      >
        <ArrowUpRightIcon className='w-4 h-4' />
        Pasar a recobro
      </button>
      <HelpTip title='AR o recobro' className='ml-1'>
        En AR el expediente lo llevas tú, con tu número y tu dominio. Pasarlo a recobro se lo entrega a ClaimUp, que
        reclama en su nombre: tus tareas pendientes pasan a ClaimUp y ya no le escribes tú.
      </HelpTip>

      {confirming && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4' role='dialog' aria-modal='true'>
          <div className='bg-white rounded-lg shadow-lg max-w-md w-full p-6 space-y-4'>
            <h3 className='text-lg font-semibold text-gray-800'>¿Pasar este expediente a recobro?</h3>
            <ul className='text-sm text-gray-600 space-y-1 list-disc pl-5'>
              <li>Lo gestionará ClaimUp como recobro extrajudicial y entrará en «En curso».</li>
              <li>Sus tareas abiertas y las propuestas de la IA pendientes dejan de ser vuestras.</li>
              <li>Desde el mes que viene ya no cuenta en vuestra cartera AR.</li>
              <li className='font-medium text-gray-800'>No se puede deshacer.</li>
            </ul>
            <label className='block text-sm text-gray-700'>
              Motivo (opcional)
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                maxLength={500}
                rows={2}
                className='mt-1 w-full rounded-lg border-gray-300 text-sm'
              />
            </label>
            <div className='flex justify-end gap-2'>
              <button
                type='button'
                onClick={() => setConfirming(false)}
                disabled={sending}
                className='px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100'
              >
                Cancelar
              </button>
              <button
                type='button'
                onClick={escalate}
                disabled={sending}
                className='px-4 py-2 rounded-lg text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60'
              >
                {sending ? 'Pasando…' : 'Sí, pasar a recobro'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EscalateToRecoveryButton;
